import {
  Captions,
  ChevronDown,
  CircleCheck,
  CircleDot,
  ClipboardCheck,
  ClipboardList,
  FileText,
  Film,
  FolderOpen,
  MessageSquare,
  Palette,
  RotateCcw,
  Scissors,
  StickyNote,
  Type,
  Volume2,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";

import { VideosSection } from "@/components/VideosSection";
import { MediaPlayer } from "@/components/MediaPlayer";
import { Button, ButtonGroup, Checkbox, Dialog, Field, Textarea } from "@/components/ui";
import { useInvalidateVideoProjectEventLogs } from "@/containers/Editor/VideoProjectPage/components/VideoProjectHistoryPanel";
import { cn } from "@/lib/utils";
import { Media, VideoProject, VideoProjectServices } from "@/services/bedrock";
import { editorQueueListUrl, getErrorMessage } from "@/utils";
import { StylePreferencesOptions } from "@/utils/projectHelpers";
import { buildQaIssues, EMPTY_QA_ISSUE_DRAFT, QaIssueDraft } from "@/components/VideoProjectRejectQaDialog/qaIssues";

import { ClientNotesComponent } from "../ClientNotesComponent";
import { StylePreferencesComponent } from "../StylePreferencesComponent";
import { VideoProjectBubbleDetails } from "../BubbleDetails";

const PLAYBACK_RATES = [1, 1.5, 2] as const;

const QA_ISSUE_ICONS: Record<VideoProject.QaIssueCategory, LucideIcon> = {
  MISSED_INSTRUCTIONS: ClipboardList,
  AUDIO: Volume2,
  CAPTIONS: Captions,
  B_ROLL: Film,
  POST_CAPTION: Type,
  CUTS_AND_TRANSITIONS: Scissors,
  OTHER: CircleDot,
};

interface VideoProjectQaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoProject: VideoProject;
  setVideoProject: React.Dispatch<React.SetStateAction<VideoProject | undefined>>;
  playerNotes?: VideoProject.ClientNote;
  coachNotes?: VideoProject.ClientNote;
  onNotesUpdate: (playerNotes?: VideoProject.ClientNote, coachNotes?: VideoProject.ClientNote) => void;
  preferencesInfo: StylePreferencesOptions[];
  formatHexColor: (color: string) => string | null;
  videoProjectMessages?: VideoProject.Message[];
  needsAck: boolean;
  refreshMessages: () => void;
  finalMessage: string;
  hashtags: string;
}

function latestEditedVideo(editedMedia?: Media[]): Media | undefined {
  const videos = (editedMedia || []).filter(media => media.category === "VIDEO");
  return [...videos].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).at(-1);
}

function transcriptFromMedia(media?: Media): string | undefined {
  const texts = media?.transcripts?.map(transcript => transcript.content).filter(Boolean) as string[] | undefined;
  if (!texts?.length) return undefined;
  return [...texts].sort((a, b) => b.length - a.length)[0];
}

function DetailSection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <details className="group rounded-xl border border-slate-200 bg-white transition-colors open:border-indigo-200 open:bg-indigo-50/30">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-sm font-medium text-slate-800 [&::-webkit-details-marker]:hidden">
        <span className="flex min-w-0 items-center gap-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition-colors group-open:bg-indigo-100 group-open:text-indigo-600">
            <Icon className="size-3.5" />
          </span>
          {title}
        </span>
        <ChevronDown className="size-3.5 shrink-0 text-slate-400 transition-transform group-open:rotate-180" />
      </summary>
      <div className="px-3 pb-3">{children}</div>
    </details>
  );
}

function QaIssueCard({
  category,
  selected,
  comment,
  onToggle,
  onCommentChange,
}: {
  category: VideoProject.QaIssueCategory;
  selected: boolean;
  comment: string;
  onToggle: (checked: boolean) => void;
  onCommentChange: (comment: string) => void;
}) {
  const id = React.useId();
  const Icon = QA_ISSUE_ICONS[category];
  const label = VideoProject.QaIssueCategoryMeta[category].label;

  return (
    <div
      className={cn(
        "rounded-xl border p-3 transition-all",
        selected
          ? "border-amber-300 bg-amber-50/80 shadow-sm shadow-amber-500/10"
          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/80"
      )}
    >
      <div className="flex items-center gap-3">
        <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
          <span
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors",
              selected ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"
            )}
          >
            <Icon className="size-4" />
          </span>
          <span className="min-w-0 text-sm font-semibold text-slate-800">{label}</span>
        </label>
        <Checkbox id={id} checked={selected} onCheckedChange={checked => onToggle(checked === true)} />
      </div>
      {selected ? (
        <Field className="mt-3" label="Comment">
          {controlProps => (
            <Textarea
              {...controlProps}
              autoSize
              minRows={2}
              maxRows={4}
              value={comment}
              placeholder="What should be fixed?"
              onChange={event => onCommentChange(event.target.value)}
            />
          )}
        </Field>
      ) : null}
    </div>
  );
}

export function VideoProjectQaModal({
  open,
  onOpenChange,
  videoProject,
  setVideoProject,
  playerNotes,
  coachNotes,
  onNotesUpdate,
  preferencesInfo,
  formatHexColor,
  videoProjectMessages,
  needsAck,
  refreshMessages,
  finalMessage,
  hashtags,
}: VideoProjectQaModalProps) {
  const history = useHistory();
  const invalidateEventLogs = useInvalidateVideoProjectEventLogs();
  const [playbackRate, setPlaybackRate] = React.useState<(typeof PLAYBACK_RATES)[number]>(1);
  const [draft, setDraft] = React.useState<QaIssueDraft>(EMPTY_QA_ISSUE_DRAFT);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) setDraft(EMPTY_QA_ISSUE_DRAFT);
  }, [open]);

  const editedVideo = latestEditedVideo(videoProject.editedMedia);
  const transcript = transcriptFromMedia(editedVideo);
  const issues = buildQaIssues(draft);
  const caption = [finalMessage, hashtags].filter(Boolean).join(" ");
  const hasIssues = issues.length > 0;

  const applyUpdatedProject = (updated: VideoProject) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        status: updated.status ?? prev.status,
        editorUserId: updated.editorUserId ?? prev.editorUserId,
        qaUserId: updated.qaUserId ?? prev.qaUserId,
      };
    });
    invalidateEventLogs(videoProject.id);
  };

  const runEvent = async (event: VideoProject.Event, nextIssues?: VideoProject.QaIssue[]) => {
    setSaving(true);
    try {
      const updated = await VideoProjectServices.update(videoProject.id, {
        event,
        ...(nextIssues ? { issues: nextIssues } : {}),
      });
      applyUpdatedProject(updated);
      return updated;
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to update project");
      return undefined;
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    const updated = await runEvent(VideoProject.Event.APPROVE_QA);
    if (!updated) return;
    toast.success("Project sent to the client");
    onOpenChange(false);
  };

  const handleReject = async () => {
    if (issues.length === 0) return;
    const updated = await runEvent(VideoProject.Event.REJECT_QA, issues);
    if (!updated) return;
    toast.success("Project sent back for edits");
    onOpenChange(false);
    history.replace(editorQueueListUrl());
  };

  const toggleCategory = (category: VideoProject.QaIssueCategory, checked: boolean) => {
    setDraft(current => ({
      ...current,
      selected: { ...current.selected, [category]: checked },
    }));
  };

  const setComment = (category: VideoProject.QaIssueCategory, comment: string) => {
    setDraft(current => ({
      ...current,
      comments: { ...current.comments, [category]: comment },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="2xl" showCloseButton className="overflow-hidden">
      <Dialog.Header className="relative overflow-hidden border-b-0 pb-3">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-indigo-50/90 via-white to-violet-50/60" />
        <div className="relative flex items-center gap-3 pr-8">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-sm shadow-indigo-500/30">
            <ClipboardCheck className="size-5" />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <Dialog.Title>QA review</Dialog.Title>
            <Dialog.Description>Watch the cut, mark issues, then send it back or to the client.</Dialog.Description>
          </div>
        </div>
      </Dialog.Header>
      <Dialog.Content className="grid max-h-[70vh] grid-cols-1 gap-6 overflow-y-auto pt-1 lg:grid-cols-2">
        <div className="flex flex-col gap-3">
          <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950 shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-slate-900 px-3 py-2">
              <span className="text-xs font-semibold tracking-wide text-slate-200">Edited video</span>
              <ButtonGroup className="rounded-lg border border-slate-700 bg-slate-800 p-0.5">
                {PLAYBACK_RATES.map(rate => (
                  <Button
                    key={rate}
                    type="button"
                    size="xs"
                    variant={playbackRate === rate ? "secondary" : "ghost"}
                    className={cn(
                      "text-slate-300 hover:bg-slate-700 hover:text-white",
                      playbackRate === rate && "bg-white text-slate-900 shadow-xs hover:bg-white hover:text-slate-900"
                    )}
                    onClick={() => setPlaybackRate(rate)}
                  >
                    {rate}x
                  </Button>
                ))}
              </ButtonGroup>
            </div>
            {editedVideo ? (
              <div className="bg-black [&_.mediaContainer]:block [&_.mediaContainer]:w-full [&_video]:block [&_video]:w-full">
                <MediaPlayer media={editedVideo} playbackRate={playbackRate} />
              </div>
            ) : (
              <p className="flex aspect-video items-center justify-center px-4 text-center text-sm text-slate-400">
                No edited video is available.
              </p>
            )}
          </div>
          {transcript ? (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50/80">
              <div className="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2">
                <FileText className="size-3.5 text-slate-400" />
                <span className="text-xs font-semibold text-slate-600">Transcript</span>
              </div>
              <div className="max-h-40 overflow-y-auto whitespace-pre-wrap px-3 py-2.5 text-sm leading-relaxed text-slate-600">
                {transcript}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold text-slate-800">Mark issues to send back</span>
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  hasIssues ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700"
                )}
              >
                {hasIssues ? `${issues.length} selected` : "Looking good"}
              </span>
            </div>
            {VideoProject.QaIssueCategories.map(category => (
              <QaIssueCard
                key={category}
                category={category}
                selected={Boolean(draft.selected[category])}
                comment={draft.comments[category] ?? ""}
                onToggle={checked => toggleCategory(category, checked)}
                onCommentChange={comment => setComment(category, comment)}
              />
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Project details</span>
            <DetailSection title="Caption" icon={Type}>
              <p className="whitespace-pre-wrap text-sm text-slate-600">{caption || "No caption"}</p>
            </DetailSection>
            <DetailSection title="Notes" icon={StickyNote}>
              <ClientNotesComponent
                playerId={videoProject.playerId}
                coachId={videoProject.coachId}
                playerNotes={playerNotes}
                coachNotes={coachNotes}
                onUpdate={onNotesUpdate}
              />
            </DetailSection>
            <DetailSection title="Preferences" icon={Palette}>
              <StylePreferencesComponent preferencesInfo={preferencesInfo} formatHexColor={formatHexColor} />
            </DetailSection>
            {videoProjectMessages ? (
              <DetailSection title="Messages" icon={MessageSquare}>
                <VideoProjectBubbleDetails
                  onSuccess={refreshMessages}
                  videoProjectId={videoProject.id}
                  submitBtnLabel="Add Message"
                  messages={videoProjectMessages}
                  needsAck={needsAck}
                />
              </DetailSection>
            ) : null}
            {videoProject.originalMedia?.length ? (
              <DetailSection title="Original files" icon={FolderOpen}>
                <VideosSection
                  type="ORIGINAL"
                  videos={videoProject.originalMedia}
                  videoProjectId={videoProject.id}
                  isEditor
                />
              </DetailSection>
            ) : null}
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Footer className="sm:items-center sm:justify-between">
        <p className="hidden text-xs text-slate-500 sm:block">
          {hasIssues
            ? "Selected issues will be sent back with the project."
            : "Approve this cut to send it to the client."}
        </p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button
            variant={hasIssues ? "default" : "outline"}
            className={cn(hasIssues && "border-transparent bg-amber-500 text-white hover:bg-amber-600")}
            onClick={() => void handleReject()}
            disabled={saving || !hasIssues}
          >
            <RotateCcw />
            Send back for edits
          </Button>
          <Button
            variant={hasIssues ? "outline" : "default"}
            className={cn(!hasIssues && "bg-emerald-600 text-white hover:bg-emerald-700")}
            onClick={() => void handleApprove()}
            disabled={saving}
            loading={saving}
          >
            <CircleCheck />
            Send to client
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
}
